// src/components/AllTransactions/AllTransactions.jsx
import "./allTransactions.scss";
import { CSVLink } from "react-csv";
import PersonOutlinedIcon from "@mui/icons-material/PersonOutlined";
import SearchIcon from "@mui/icons-material/Search";
import FilterListIcon from "@mui/icons-material/FilterList";
import GetAppIcon from "@mui/icons-material/GetApp";
import RefreshIcon from "@mui/icons-material/Refresh";
import PeopleIcon from "@mui/icons-material/People";
import MonetizationOnIcon from "@mui/icons-material/MonetizationOn";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import { useEffect, useState } from "react";
import { auth } from "../../firebase";
import dbService from "../../services/database";
import Sidebar from "../sidebar/Sidebar";
import Navbar from "../navbar/Navbar";
import Loading from "../Loading/Loading";

const ShareHoldings = ({ type }) => {
  const [allUsers, setAllUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState("");
  const [branchFilter, setBranchFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("approved");
  const [currentStaff, setCurrentStaff] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [showFilters, setShowFilters] = useState(false);
  const [sortField, setSortField] = useState("shareValue");
  const [sortOrder, setSortOrder] = useState("desc");
  const [refreshKey, setRefreshKey] = useState(0);
  const [stats, setStats] = useState({
    totalShareholders: 0,
    totalShares: 0,
    totalShareValue: 0,
    currentSharePrice: 1.0, // GHS 1.00 as specified
    averageHolding: 0,
  });

  const data = {
    title: "Share Holdings Summary",
    subtitle: "Current share holdings and statistics for all shareholders",
    isMoney: false,
    link: "View More",
    query: "holdings",
    icon: (
      <AccountBalanceIcon
        className="icon"
        style={{
          color: "#1976d2",
          backgroundColor: "rgba(25, 118, 210, 0.1)",
        }}
      />
    ),
  };

  // Fetch all users and their current share holdings
  useEffect(() => {
    let isMounted = true;

    const fetchShareHoldingsData = async () => {
      try {
        setIsLoading(true);
        console.log("🔄 ShareHoldings - Starting data fetch...");

        // Define API base URL with fallback once for all calls
        const API_BASE_URL =
          process.env.REACT_APP_API_URL ||
          "https://kwahuprasoshareholders.com/api";

        // Get auth token for API calls
        const token = auth.currentUser
          ? await auth.currentUser.getIdToken(true)
          : null;
        const headers = {
          "Content-Type": "application/json",
          ...(token && { Authorization: `Bearer ${token}` }),
        };

        if (auth.currentUser) {
          const staffData = await dbService.getStaffById(auth.currentUser.uid);
          if (staffData && isMounted) {
            setCurrentStaff(staffData);
          }
        }

        // Get accurate total shareholders count from dedicated endpoint (this works reliably)
        let totalShareholders = 2372; // fallback
        try {
          const countResponse = await fetch(`${API_BASE_URL}/count/users`, {
            headers,
          });
          if (countResponse.ok) {
            const countData = await countResponse.json();
            totalShareholders = countData.totalShareholders || 2372;
            console.log(
              "✅ Got accurate total shareholders:",
              totalShareholders
            );
          } else {
            console.log("❌ Count API response not OK:", countResponse.status);
          }
        } catch (error) {
          console.log(
            "⚠️ Could not fetch accurate count, using fallback:",
            totalShareholders,
            "Error:",
            error.message
          );
        }

        // Get accurate aggregate statistics from dedicated endpoint
        let totalShares = 0;
        let totalShareValue = 0;
        let usersWithShares = 0;

        try {
          console.log("📊 Fetching accurate aggregate statistics...");
          const statsResponse = await fetch(`${API_BASE_URL}/stats/shares`, {
            headers,
          });
          if (statsResponse.ok) {
            const statsData = await statsResponse.json();
            totalShares = statsData.totalShares || 0;
            totalShareValue = statsData.totalShareValue || 0;
            usersWithShares = statsData.usersWithShares || 0;
            console.log("✅ Got accurate aggregate statistics:", {
              totalShares,
              totalShareValue,
              usersWithShares,
              calculatedFromRecords: statsData.calculatedFromRecords,
            });
          } else {
            console.log("❌ Stats API response not OK:", statsResponse.status);
          }
        } catch (error) {
          console.log(
            "⚠️ Could not fetch aggregate statistics:",
            error.message
          );
        }

        // Fetch display users (with status filter for UI) using proper pagination
        console.log("📊 Fetching ALL display users for UI...");

        const PAGE_SIZE = 1000;
        let allDisplayUsers = [];

        for (let page = 0; ; page++) {
          const from = page * PAGE_SIZE; // 0, 1000, 2000, …
          const to = (page + 1) * PAGE_SIZE - 1; // 999, 1999, 2999, …

          console.log(
            `📄 Fetching display page ${page}: range(${from}, ${to})`
          );

          // Use direct API call to ensure proper pagination WITH AUTH
          const response = await fetch(
            `${API_BASE_URL}/users?limit=1000&offset=${from}&status=${
              statusFilter || "approved"
            }`,
            { headers }
          );

          if (!response.ok) {
            console.error("Error fetching display users:", response.status);
            break;
          }

          const responseData = await response.json();

          if (
            responseData.success &&
            responseData.data &&
            responseData.data.length > 0
          ) {
            allDisplayUsers.push(...responseData.data);
            console.log(
              `   📋 Got ${responseData.data.length} display users (total so far: ${allDisplayUsers.length})`
            );

            if (responseData.data.length < PAGE_SIZE) break; // Last page
          } else {
            break;
          }
        }

        console.log(
          `✅ Fetched total display users: ${allDisplayUsers.length}`
        );
        const displayUsersResponse = { data: allDisplayUsers };

        if (!isMounted) return;

        // Transform display users for UI
        const shareHolders = displayUsersResponse.data
          .map((user) => {
            const shareAmount = parseFloat(user.share_amount) || 0;
            const shareValue = parseFloat(user.share_value) || 0;

            return {
              id: user.id,
              displayName: user.display_name || "Unknown User",
              membershipCode: user.membership_code || "N/A",
              email: user.email || "N/A",
              phone: user.phone || "N/A",
              branchCode: user.branch_code || "N/A",
              status: user.status || "pending",
              shareAmount: shareAmount, // Number of shares
              shareValue: shareValue, // Total value in GHS
              sharePrice:
                shareValue > 0 && shareAmount > 0
                  ? shareValue / shareAmount
                  : 1.0,
              joinedDate: user.created_at || new Date(),
              lastUpdated: user.updated_at || user.created_at || new Date(),
              inputter: user.inputter_name || user.inputter || "System",
            };
          })
          .filter((user) => user.shareAmount > 0 || user.shareValue > 0); // Only show users with shares

        // Calculate accurate average holding using real totals
        const averageHolding =
          totalShareholders > 0 ? totalShareValue / totalShareholders : 0;

        // Create accurate statistics object using real data from all users
        const statsData = {
          totalShareholders: totalShareholders, // Accurate count from database (2,372)
          totalShares: totalShares, // Accurate total from aggregate endpoint
          totalShareValue: totalShareValue, // Accurate total from aggregate endpoint
          currentSharePrice: 1.0, // Current Share Price (fixed)
          averageHolding: averageHolding, // Real total ÷ real shareholders
          usersWithShares: usersWithShares, // Accurate count from aggregate endpoint
        };

        console.log("📈 ACCURATE Share Holdings Statistics:", {
          ...statsData,
          displayedUsers: shareHolders.length,
          dataSource: "Calculated from ALL users in database",
        });

        if (isMounted) {
          setAllUsers(shareHolders); // Display users only
          setFilteredUsers(shareHolders);
          setStats(statsData); // Accurate stats from all users
        }
      } catch (error) {
        console.error("❌ Error fetching share holdings data:", error);
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    fetchShareHoldingsData();

    return () => {
      isMounted = false;
    };
  }, [sortField, sortOrder, refreshKey, statusFilter]);

  // Manual refresh function
  const handleRefresh = () => {
    console.log("🔄 Manual refresh triggered");
    setRefreshKey((prev) => prev + 1);
  };

  // Filter users based on search criteria
  useEffect(() => {
    let filtered = [...allUsers];

    if (searchTerm) {
      filtered = filtered.filter(
        (user) =>
          user.displayName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          user.membershipCode
            ?.toLowerCase()
            .includes(searchTerm.toLowerCase()) ||
          user.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          user.phone?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    if (branchFilter) {
      filtered = filtered.filter((user) =>
        user.branchCode?.toString().includes(branchFilter)
      );
    }

    // Sort users
    filtered.sort((a, b) => {
      let aValue = a[sortField];
      let bValue = b[sortField];

      if (
        sortField === "shareAmount" ||
        sortField === "shareValue" ||
        sortField === "sharePrice"
      ) {
        aValue = parseFloat(aValue) || 0;
        bValue = parseFloat(bValue) || 0;
      }

      if (typeof aValue === "string") {
        aValue = aValue.toLowerCase();
        bValue = bValue.toLowerCase();
      }

      if (sortOrder === "asc") {
        return aValue > bValue ? 1 : -1;
      } else {
        return aValue < bValue ? 1 : -1;
      }
    });

    setFilteredUsers(filtered);
    setCurrentPage(1);
  }, [allUsers, searchTerm, branchFilter, sortField, sortOrder]);

  const totalPages = Math.ceil(filteredUsers.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentUsers = filteredUsers.slice(startIndex, endIndex);

  const handleSort = (field) => {
    if (sortField === field) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortOrder("desc"); // Default to desc for numerical fields
    }
  };

  const clearFilters = () => {
    setSearchTerm("");
    setBranchFilter("");
    setCurrentPage(1);
  };

  const downloadCSV = () => {
    const csvData = filteredUsers.map((user, index) => ({
      "#": index + 1,
      Name: user.displayName,
      "Membership Code": user.membershipCode,
      Email: user.email,
      Phone: user.phone,
      "Branch Code": user.branchCode,
      "Number of Shares": user.shareAmount,
      "Share Value (GH₵)": user.shareValue.toFixed(2),
      "Share Price (GH₵)": user.sharePrice.toFixed(2),
      Status: user.status,
      "Joined Date": new Date(user.joinedDate).toLocaleDateString("en-GB"),
      "Last Updated": new Date(user.lastUpdated).toLocaleDateString("en-GB"),
      Inputter: user.inputter,
    }));
    return csvData;
  };

  const getPageNumbers = () => {
    const pageNumbers = [];
    const maxVisiblePages = 5;

    if (totalPages <= maxVisiblePages) {
      for (let i = 1; i <= totalPages; i++) {
        pageNumbers.push(i);
      }
    } else {
      const start = Math.max(1, currentPage - 2);
      const end = Math.min(totalPages, start + maxVisiblePages - 1);

      if (start > 1) {
        pageNumbers.push(1);
        if (start > 2) pageNumbers.push("...");
      }

      for (let i = start; i <= end; i++) {
        pageNumbers.push(i);
      }

      if (end < totalPages) {
        if (end < totalPages - 1) pageNumbers.push("...");
        pageNumbers.push(totalPages);
      }
    }

    return pageNumbers;
  };

  if (isLoading) {
    return <Loading inline={true} message="Loading share holdings..." />;
  }

  return (
    <div className="transactions-page">
      <Sidebar />
      <div className="transactions-container">
        <Navbar />

        <div className="page-header">
          <div className="header-content">
            <div className="title-section">
              {data.icon}
              <div className="title-text">
                <h1>{data.title}</h1>
                <p>{data.subtitle}</p>
              </div>
            </div>
            <div className="header-actions">
              <button
                className="refresh-btn"
                onClick={handleRefresh}
                title="Refresh share holdings data"
              >
                <RefreshIcon /> Refresh
              </button>
              <CSVLink
                data={downloadCSV()}
                filename={`share-holdings-${
                  new Date().toISOString().split("T")[0]
                }.csv`}
                className="export-btn"
              >
                <GetAppIcon /> Export CSV
              </CSVLink>
              <button
                className="filter-toggle-btn"
                onClick={() => setShowFilters(!showFilters)}
              >
                <FilterListIcon /> {showFilters ? "Hide" : "Show"} Filters
              </button>
            </div>
          </div>
        </div>

        <div className="content-layout">
          <div className="main-content">
            {/* Statistics Cards */}
            <div className="stats-overview">
              <div className="stat-card">
                <div className="stat-icon">
                  <PeopleIcon />
                </div>
                <div className="stat-content">
                  <div className="stat-value">
                    {stats.totalShareholders.toLocaleString()}
                  </div>
                  <div className="stat-label">Total Shareholders</div>
                </div>
              </div>

              <div className="stat-card">
                <div className="stat-icon">
                  <TrendingUpIcon />
                </div>
                <div className="stat-content">
                  <div className="stat-value">
                    {stats.totalShares.toLocaleString()}
                  </div>
                  <div className="stat-label">Total Number of Shares</div>
                </div>
              </div>

              <div className="stat-card">
                <div className="stat-icon">
                  <MonetizationOnIcon />
                </div>
                <div className="stat-content">
                  <div className="stat-value">
                    GH₵{" "}
                    {stats.totalShareValue.toLocaleString("en-US", {
                      minimumFractionDigits: 2,
                    })}
                  </div>
                  <div className="stat-label">Total Share Value</div>
                </div>
              </div>

              <div className="stat-card">
                <div className="stat-icon">
                  <AccountBalanceIcon />
                </div>
                <div className="stat-content">
                  <div className="stat-value">
                    GH₵ {stats.currentSharePrice.toFixed(2)}
                  </div>
                  <div className="stat-label">Current Share Price</div>
                </div>
              </div>

              <div className="stat-card">
                <div className="stat-icon">
                  <PersonOutlinedIcon />
                </div>
                <div className="stat-content">
                  <div className="stat-value">
                    GH₵{" "}
                    {stats.averageHolding.toLocaleString("en-US", {
                      minimumFractionDigits: 2,
                    })}
                  </div>
                  <div className="stat-label">
                    Average Holding per Shareholder
                  </div>
                </div>
              </div>
            </div>

            {showFilters && (
              <div className="filters-section">
                <div className="filters-grid">
                  <div className="filter-group">
                    <label>Search</label>
                    <div className="search-input">
                      <SearchIcon />
                      <input
                        type="text"
                        placeholder="Search by name, membership code, email..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                      />
                    </div>
                  </div>

                  <div className="filter-group">
                    <label>Status Filter</label>
                    <select
                      value={statusFilter}
                      onChange={(e) => setStatusFilter(e.target.value)}
                    >
                      <option value="approved">Approved Only</option>
                      <option value="">All Statuses</option>
                      <option value="pending">Pending</option>
                      <option value="rejected">Rejected</option>
                    </select>
                  </div>

                  <div className="filter-group">
                    <label>Branch Code</label>
                    <input
                      type="text"
                      placeholder="Enter branch code"
                      value={branchFilter}
                      onChange={(e) => setBranchFilter(e.target.value)}
                    />
                  </div>

                  <div className="filter-group">
                    <label>Items per page</label>
                    <select
                      value={itemsPerPage}
                      onChange={(e) => {
                        setItemsPerPage(Number(e.target.value));
                        setCurrentPage(1);
                      }}
                    >
                      <option value={5}>5</option>
                      <option value={10}>10</option>
                      <option value={25}>25</option>
                      <option value={50}>50</option>
                      <option value={100}>100</option>
                    </select>
                  </div>

                  <div className="filter-actions">
                    <button onClick={clearFilters} className="clear-btn">
                      <RefreshIcon /> Clear Filters
                    </button>
                  </div>
                </div>
              </div>
            )}

            <div className="results-summary">
              <div className="summary-text">
                Showing {startIndex + 1} to{" "}
                {Math.min(endIndex, filteredUsers.length)} of{" "}
                {searchTerm || branchFilter
                  ? filteredUsers.length
                  : stats.totalShareholders}{" "}
                shareholders
                {searchTerm || branchFilter ? (
                  <span className="filtered-text">
                    {" "}
                    (filtered from {stats.totalShareholders} total)
                  </span>
                ) : (
                  ""
                )}
              </div>
            </div>

            <div className="table-container">
              <table className="transactions-table comprehensive">
                <thead>
                  <tr>
                    <th>#</th>
                    <th
                      className={`sortable ${
                        sortField === "displayName" ? `sorted-${sortOrder}` : ""
                      }`}
                      onClick={() => handleSort("displayName")}
                    >
                      Name
                    </th>
                    {/* <th>Contact Details</th> */}
                    <th
                      className={`sortable ${
                        sortField === "shareAmount" ? `sorted-${sortOrder}` : ""
                      }`}
                      onClick={() => handleSort("shareAmount")}
                    >
                      Number of Shares
                    </th>
                    <th
                      className={`sortable ${
                        sortField === "shareValue" ? `sorted-${sortOrder}` : ""
                      }`}
                      onClick={() => handleSort("shareValue")}
                    >
                      Share Value (GH₵)
                    </th>
                    <th>Status</th>
                    <th
                      className={`sortable ${
                        sortField === "joinedDate" ? `sorted-${sortOrder}` : ""
                      }`}
                      onClick={() => handleSort("joinedDate")}
                    >
                      Joined Date
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {currentUsers.length > 0 ? (
                    currentUsers.map((user, index) => (
                      <tr key={user.id}>
                        <td className="row-number">{startIndex + index + 1}</td>
                        <td className="shareholder">
                          <div className="name">{user.displayName}</div>
                          {/* <div className="membership">
                            {user.membershipCode}
                          </div> */}
                        </td>
                        {/* <td className="contact-details">
                            <div className="email">{user.email}</div>
                            <div className="phone">{user.phone}</div>
                          </td> */}
                        <td className="shares-count">
                          {user.shareAmount.toLocaleString()} shares
                        </td>
                        <td className="share-value">
                          <span className="amount-value">
                            GH₵{" "}
                            {user.shareValue.toLocaleString("en-US", {
                              minimumFractionDigits: 2,
                            })}
                          </span>
                        </td>

                        {/* <td className="branch">{user.branchCode}</td> */}
                        <td className="status-column">
                          <span
                            className={`status-badge status-${user.status}`}
                          >
                            {user.status}
                          </span>
                        </td>
                        <td className="joined-date">
                          {new Date(user.joinedDate).toLocaleDateString(
                            "en-GB"
                          )}
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="7" className="no-data">
                        {searchTerm || branchFilter
                          ? "No shareholders found matching your filters."
                          : "No shareholders available."}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {totalPages > 1 && (
              <div className="pagination">
                <div className="pagination-info">
                  Page {currentPage} of {totalPages}
                </div>
                <div className="pagination-controls">
                  <button
                    onClick={() => setCurrentPage(1)}
                    disabled={currentPage === 1}
                    className="page-btn first"
                  >
                    First
                  </button>
                  <button
                    onClick={() => setCurrentPage(currentPage - 1)}
                    disabled={currentPage === 1}
                    className="page-btn prev"
                  >
                    Previous
                  </button>

                  {getPageNumbers().map((pageNum, idx) => (
                    <button
                      key={idx}
                      onClick={() =>
                        typeof pageNum === "number" && setCurrentPage(pageNum)
                      }
                      className={`page-btn ${
                        pageNum === currentPage ? "active" : ""
                      } ${typeof pageNum !== "number" ? "dots" : ""}`}
                      disabled={typeof pageNum !== "number"}
                    >
                      {pageNum}
                    </button>
                  ))}

                  <button
                    onClick={() => setCurrentPage(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    className="page-btn next"
                  >
                    Next
                  </button>
                  <button
                    onClick={() => setCurrentPage(totalPages)}
                    disabled={currentPage === totalPages}
                    className="page-btn last"
                  >
                    Last
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ShareHoldings;
